import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { isUserMultiTenant, userHasPermission } from '../../../../../swzutils/UserPermissionsUtil';
import { Button } from "antd";
import { DownloadOutlined, EyeOutlined, LinkOutlined, PlusOutlined, ReloadOutlined, TruckOutlined } from "@ant-design/icons";
import ActiveAtStatusTag from "../../../components/ActiveAtStatusTag";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import PermissionButton from "../../../components/PermissionButton";
import CustomerUpdateModal from "../CustomerUpdateModal";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import { showErrorNotification } from "../../../../../swzutils";
import { formatName } from "../../../../../util/FormatUtil";
import multiTextTableFilter from "../../../components/table/MultiTextTableFilter";
import { useAppSelector } from "../../../../../store";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
const CustomerTable = () => {
    const tableRef = useRef(null);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const downloadCustomersCsv = () => {
        tableRef.current?.downloadCurrentFilterAsCsv("Customers");
    };
    const downloadCustomersContactsCsv = (customer) => {
        tableRef.current?.downloadCurrentFilterAsCsv("Customer Contacts for " + customer.name, `/${customer.id}/contacts/csv`);
    };
    const onAddCustomerClicked = () => {
        if (selectedTenant && selectedTenant.id) {
            setShowUpdateModal(true);
        }
        else {
            showErrorNotification("Please select a profile to which you wish to add a customer");
        }
    };
    const hideUpdateModal = () => {
        setShowUpdateModal(false);
    };
    const columns = [{
            key: "code",
            title: 'Code',
            dataIndex: 'code',
            sorter: false,
            width: '5%',
            filter: {
                type: 'text',
                requestKey: 'codeLike',
                placeholder: 'Code',
            }
        }, {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
            width: '22%',
            filter: {
                type: 'text',
                requestKey: 'nameLike',
                placeholder: 'Name',
            }
        }, {
            key: 'group',
            title: 'Group',
            dataIndex: ['group', 'name'],
            width: '15%',
            filter: {
                type: 'text',
                requestKey: 'groupNameLike',
                placeholder: 'Group name',
            }
        }, {
            key: 'tier',
            title: 'Tier',
            dataIndex: 'tier',
            width: '7%',
        }, {
            key: 'debtor',
            title: 'Debtor',
            dataIndex: ['debtor', 'name'],
            render: (text, record) => {
                if (record.debtor && userHasPermission(loggedInUser, ['admin:debtor:view'])) {
                    return _jsxs("a", { href: `#/app/debtors/edit/${record.debtor.id}`, children: [record.debtor.name, " ", _jsx(LinkOutlined, {})] });
                }
                return text;
            },
            width: '15%',
        }, {
            key: 'salesPerson',
            title: 'Sales Person',
            dataIndex: ['salesPerson', 'salesAdminUser', 'id'],
            filter: {
                type: 'custom',
                requestKey: 'salesPersonNameLike',
                builder: multiTextTableFilter([{
                        name: 'First Name',
                        fetchKey: 'salesPersonFirstNameLike'
                    }, {
                        name: 'Last Name',
                        fetchKey: 'salesPersonLastNameLike'
                    }], false)
            },
            render: (text, record) => {
                return formatName(record?.salesPerson?.user?.firstName, record?.salesPerson?.user?.lastName);
            },
            width: '15%'
        }, {
            key: "tenantId",
            title: 'Profile',
            dataIndex: ['tenant', 'name'],
            condition: isUserMultiTenant(loggedInUser),
            width: '15%'
        }, {
            title: 'Active',
            dataIndex: 'enabled',
            filter: {
                type: 'enum',
                requestKey: 'enabled',
                enums: [{
                        text: 'Active',
                        value: true,
                    }, {
                        text: 'Inactive',
                        value: false,
                    }],
                filterMultiple: false,
            },
            render: (text, record) => (_jsx(ActiveAtStatusTag, { inactiveAt: record.disabledAt }))
        }, {
            title: 'Action',
            key: 'action',
            width: '10%',
            condition: userHasPermission(loggedInUser, ['admin:customer:update']),
            render: (text, record) => (_jsx(PermissionDropdownButton, { permissions: ['admin:customer:view'], route: `#/app/customers/edit/${record.id}`, label: _jsxs(_Fragment, { children: [_jsx(EyeOutlined, {}), " View"] }), menuItems: [{
                        label: 'Customer Contacts CSV',
                        key: 'customerContactsCsv',
                        icon: _jsx(DownloadOutlined, {}),
                        onClick: () => {
                            downloadCustomersContactsCsv(record);
                        },
                        permissions: ['admin:customerContactExport:csv'],
                    }, {
                        label: 'Vehicles',
                        key: 'customerVehiclesMenu',
                        icon: _jsx(TruckOutlined, {}),
                        route: `#/app/customers/${record.id}/vehicles`,
                        permissions: ['admin:customerContactExport:csv'],
                    }] }))
        }];
    return (_jsxs("article", { className: "article swz-fixed-table-article", children: [_jsx("h2", { className: "article-title", children: "Customers" }), _jsxs("div", { className: "table-operations clearfix", children: [_jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) }), _jsx(PermissionButton, { permissions: ['admin:customer:create'], onClick: onAddCustomerClicked, icon: _jsx(PlusOutlined, {}), children: "Add a Customer" }), _jsx(PermissionButton, { permissions: ['admin:customer:csv'], className: "pull-right", onClick: downloadCustomersCsv, title: "Customers CSV", icon: _jsx(DownloadOutlined, {}), children: "Customers CSV" }), _jsx(PermissionButton, { permissions: ['admin:customerGroup:list'], href: "#/app/customers/groups/list", title: "Manage Customer Groups", children: "Manage Customer Groups" })] }), _jsx(PaginatedFilterableTable, { tableKey: 'customersTable', ref: tableRef, dataUri: "/admin/v1/customers", columns: columns, refreshOnSelectedTenantChanged: true, initialFilters: {
                    enabled: [true]
                } }), _jsx(CustomerUpdateModal, { open: showUpdateModal, tenantId: selectedTenant?.id, onSave: ( /*customer*/) => {
                    hideUpdateModal();
                    refresh();
                }, onCancel: hideUpdateModal })] }));
};
export default CustomerTable;
